import { useState, useEffect, useRef, useMemo } from "react";
import { message, Modal, Row, Col } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import sortBy from "lodash/sortBy";
import db from "../../../../database";
import { paymentProcess } from "../payment/paymentProcess";
import { addAmount, completePayment } from "../payment/paymentController";
import PoleDisplay from "../../../../lib/printer/poleDisplay";
import CWPoleDisplay from "../../../../lib/printer/CWPoleDisplay";
import styles from "../../../../constants/styles.json";
import messages from "../../../../errors/messages.en-IN.json";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { dateValidator, currentDay, timeStamp, timeValidator } from "../../../../utils/clock";
import useSafeTimeout from "../../../../hooks/useSafeTimeout";
import { TotalBillDiscount } from "../PricingRules/totalBillDiscount";
import { TotalBillFreeProductDiscount } from "../PricingRules/totalBillFreeProductDiscount";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import { isServerReachable } from "../../../../utils/serverReachability";
import { upsertGiftCardTransaction } from "../../../../services";
import { buildGiftCardReversalPayload } from "../../../../utils/giftCardReversal";
import upsertPOSLog from "../../Retail/posLog";
import useCartStore from "../../../../stores/useCartStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useTransactionStore from "../../../../stores/useTransactionStore";

/**
 * usePaymentFlow — extracted from Core/index.js (~lines 5100–6100 + 7894–8326)
 *
 * Manages payment method selection, filtering, amount handling,
 * denominations, cash/card/loyalty/gift-card flows, and requestPayment / processPayment.
 */
const usePaymentFlow = ({
  cart,
  setCart,
  tillData,
  tillDataPaymentMethods,
  posConfig,
  selectedSaleTypeData,
  precision,
  orderType,
  addProduct,
  pricePrecision,
  amount: amountProp,
  setAmount: setAmountProp,
  overPayedAmount,
  setOverPayedAmount,
  numb,
  setNumb,
  selectedProductInCart,
  setSelectedProductInCart,
  setShowPaymentMethods,
  showPaymentMethods,
  giftCardData,
  setGiftCardData,
  paymentModalInputRef,
  isExchange,
  CWHardwareController,
  setPaytmQrCodeModalOpens,
  setQrCodeResponse,
  setLoading,
  setLayoutType,
  setKioskUI,
  dispatchQRCodeEvent,
  closeQRCodeModal,
  dispatchCCAvenueEvent,
  closeCCAvenueModal,
  exchangeCart,
  setExchangeCart,
  processOrder,
  selectedDocumentType,
  setSelectedDocumentType,
  setLoader,
  tillLayout,
  setIsCardPaymentLoading,
  isCardPaymentFlag,
  setIsCardPaymentFlag,
  setCardPaymnetError,
  setCardPaymnetStatus,
  setCardPaymnetStatusPLB,
  setShowCardPaymnteModal,
  dispatchEvent,
  retailCustomerRules,
  redeemNumberRef,
  loyaltyInputRef,
  setIsInputFocused,
  searchInputRef,
  setIntegratedPaymentStatus,
  setOpen,
  setCustomMixItems,
  showCustomerWalletRef,
  handleWalletPostPaymentRef,
  isNewGiftCardFlagFromParent,
  showGiftCardTopupRef,
  handleGiftCardTopupPostPaymentRef,
  setIsNewGiftCardFlag,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  giftCardFlag,
  setGiftCardFlag,
}) => {
  const { t } = useTranslation();
  const { safeSetTimeout } = useSafeTimeout();
  const isNewGiftCardFlag = isNewGiftCardFlagFromParent;
  // Use the amount / setAmount from parent (shared state)
  const amount = amountProp;
  const setAmount = setAmountProp;

  // ─── Payment Method State ───
  const [denaminationsKeyboard, setDenaminationsKeyboard] = useState(false);
  const [paymentModal, setPaymentModalLocal] = useState(false);
  const [paymentProcessFlag, setPaymentProcessFlag] = useState(false);

  const [loyaltyPaymentOtp, setLoyaltyPaymentOtp] = useState();
  const [loyaltyInputValue, setLoyaltyInputValue] = useState("");
  const [loyalityOtpModalVisible, setLoyalityOtpModalVisible] = useState(false);
  const [loyalityOtpData, setLoyalityOtpData] = useState();
  const [paymentModalLoyalityMessages, setPaymentModalLoyalityMessages] = useState({
    inputMessage: "Enter Amount",
    buttonText: "Add Payment",
  });
  const [paymentModalByCustomerState, setPaymentModalByCustomerState] = useState(false);
  const [isGiftCardFlag, setIsGiftCardFlag] = useState(false);
  const [isWalletFlag, setIswalletFlag] = useState(false);
  const [giftCardMode, setGiftCardMode] = useState("topup"); // "topup" | "redeem"
  const [isBankTransferOpen, setIsBankTransferOpen] = useState(false);
  const [bankTransferContext, setBankTransferContext] = useState(null);
  const paymentInProgressRef = useRef(false);

  // TSK-2934: generic payment-data popup, driven by paymentMethod.enablePaymentData
  // (from getTillData) rather than the Card-specific Till Template config.
  const [showPaymentDataPopup, setShowPaymentDataPopup] = useState(false);
  const [paymentDataPopupMethod, setPaymentDataPopupMethod] = useState(null);
  const [paymentDataPopupAmount, setPaymentDataPopupAmount] = useState(0);

  // ─── Payment List / Pagination State ───
  const filterPaymentsBySaleType = (paymentMethods, id) => {
    // "ALL" sentinel bypasses the sale-type filter while keeping till
    // eligibility — used as a fallback in handleCartTotalClick when return
    // mode's active sale type yields zero methods. The Tab7 fix added this
    // sentinel to usePaymentMethods.js but THIS file ships its own local
    // copy of the function, so the fallback never triggered. (Tab8 issue #2)
    if (id === "ALL") return paymentMethods;
    const saleTypeId = id || selectedSaleTypeData?.cwrSaletype?.cwrSaletypeId;
    if (!saleTypeId) return paymentMethods;
    const tillId = tillData.tillAccess?.cwrTill?.cwrTillID;
    return paymentMethods.filter(
      ({ salesType, cwrTillId }) => (!cwrTillId || cwrTillId === tillId) && Array.isArray(salesType) && salesType.some(({ cwrSaletypeId }) => cwrSaletypeId === saleTypeId)
    );
  };

  const getFilteredPaymentMethods = (id) => {
    if (!Array.isArray(tillDataPaymentMethods)) return [];
    const isExchange = (useTransactionStore.getState().selectedReturnType || localStorage.getItem("selectedReturnType")) === "EXCHANGE";
    const exchangeCartObj = useCartStore.getState().exchangeCart || JSON.parse(localStorage.getItem("exchangeCartObj") || "{}");
    const freshCart = useCartStore.getState().cart || cart;
    const activeCart = isExchange && exchangeCartObj ? exchangeCartObj : freshCart;

    const filteredBySaleType = filterPaymentsBySaleType(tillDataPaymentMethods, id);
    const filteredMethods = sortBy(
      filteredBySaleType.filter((pm) => {
        const isLoyaltyDisabled = posConfig?.loyaltyApplicable === "N" && pm.isloyalty;
        const isNoLoyaltyLevel = !activeCart?.customer?.loyaltyLevel?.cwrLoyaltyLevelId && pm.isloyalty;
        const isNoCredit = !activeCart?.customer?.iscredit && pm.name === "Credit" && useTransactionStore.getState().isCreditSale !== "true";
        const isCafeTips = pm.name.toLowerCase() === "cafe tips";
        const loyaltyFlag = tillData?.tillAccess?.csBunit?.enableLoyalty !== "Y" && pm.isloyalty;
        const isNoWallet = !activeCart?.customer?.cwrWallet?.wallet_Id && pm.isWallet === "Y";
        const isInvalidReturnMethod = activeCart?.isReturn === true && pm.isReturns !== "Y";
        return !(isLoyaltyDisabled || isNoLoyaltyLevel || isNoCredit || isCafeTips || loyaltyFlag || isNoWallet || isInvalidReturnMethod);
      }),
      "sequenceNo"
    );
    return filteredMethods;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredPaymentMethods = useMemo(
    () => getFilteredPaymentMethods(),
    [tillDataPaymentMethods, selectedSaleTypeData, posConfig, cart.customer, cart.customer?.iscredit, cart.customer?.loyaltyLevel?.cwrLoyaltyLevelId, tillData]
  );

  const initialShowPayments = filteredPaymentMethods.length > 5 ? filteredPaymentMethods.slice(0, 5) : filteredPaymentMethods;

  // Side effect: sync initialShowPayments to localStorage only when it changes (not on every render)
  useEffect(() => {
    localStorage.setItem("initialShowPayments", JSON.stringify(initialShowPayments));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPaymentMethods]);

  const [isShowingAll, setIsShowingAll] = useState(false);
  const [showPayments, setShowPayments] = useState(initialShowPayments);
  const [startIndex, setStartIndex] = useState(initialShowPayments.length);

  // ─── handleCartTotalClick ───
  const handleCartTotalClick = async (documentType) => {
    try {
      // Read FRESH cart from store to avoid stale closure values after a
      // delete + re-add sequence (issue #11). The closure-captured `cart`
      // could be stale, causing silent early-returns on the click.
      const freshCart = useCartStore.getState().cart || cart;
      // weight > 0 excludes a deleted advance line: this app's delete zeroes an
      // item's weight instead of removing it from the array (see cart.js's own
      // "weight === 0 means the line's qty was fully removed" convention), so a
      // check without this exclusion still finds the AD item after the cashier
      // deletes it, incorrectly blocking Create Advance/Complete Order with
      // "Advance option not selected" on an order that no longer has one.
      let index = freshCart.items.findIndex((item) => item.productSegment === "AD" && item.weight > 0);
      if ((posConfig ? posConfig["requireDeliveryDate-Advance"] === "Y" : false) && (documentType === "Advance" || selectedDocumentType === "Advance")) {
        if (!freshCart.deliveryDate || freshCart.deliveryDate === "") {
          message.warning(`${messages.errors.cart.deliverydate_required}`);
          return;
        }
      }
      if (index !== -1 && selectedDocumentType !== "Advance") {
        setShowPaymentMethods(false);
        message.warning(`${messages.errors.cart.advance_option_required}`);
        return;
      }

      let cartObj = useCartStore.getState().cart || cart;
      const exchangeCartState = useCartStore.getState().exchangeCart;
      const isExchangeFlow = useTransactionStore.getState().selectedReturnType === "EXCHANGE" && exchangeCartState;

      if (isExchangeFlow && !exchangeCartState?.items?.length) {
        message.warning("Please add items to the exchange cart before proceeding to payment.");
        return;
      }

      const cartTotal = Number(cartObj.total) || Number(cart.total) || 0;
      const exchTotal = isExchangeFlow ? Number(exchangeCartState.total) || 0 : 0;
      const paidSoFar = isExchangeFlow ? Number(exchangeCartState.paid) || 0 : Number(cartObj.paid) || 0;
      setAmount(parseFloat((cartTotal + exchTotal - paidSoFar).toFixed(precision)));
      const workflowRules = (await db.POSWorkFlowRules.toArray()) || [];
      const approvers = (await db.approvers.toArray()) || [];

      let localCart = useCartStore.getState().cart || cart;

      if (!localCart.items) {
        localCart.items = [];
      }

      let PaymentMethods = getFilteredPaymentMethods() || [];
      // Defensive fallback for return mode: if the active sale-type filter
      // produced zero methods (often because the configured "return" sale
      // type doesn't list any payment methods), show the unfiltered set so
      // the cashier can still issue the refund. Without this, "Total Amount
      // To Pay" rendered an empty bar in Return & Refund mode. (Issue #4)
      if (PaymentMethods.length === 0 && (localCart?.isReturn === true || cart?.isReturn === true)) {
        PaymentMethods = getFilteredPaymentMethods("ALL") || [];
      }
      const advanceItem = cart.items.find((item) => item.type === "S" && item.weight > 0 && item.productSegment === "AD");
      if (
        cart.layAway === "Y" &&
        cart?.docType === "Advance" &&
        !advanceItem &&
        posConfig?.allowLayawaywithoutPayment !== "Y" &&
        !cart.items.some((item) => item.type === "S" && item?.productSegment === "AD" && item.weight > 0)
      ) {
        PaymentMethods = [
          ...PaymentMethods,
          ...(posConfig?.enablePreOrder !== "Y"
            ? [
                {
                  cWRPaymentMethodID: "",
                  sequenceNo: 9999,
                  finPaymentmethodId: "",
                  finFinancialAccountId: "",
                  finDayCloseAccountId: null,
                  name: "Complete Order",
                  integratedPayment: false,
                  isloyalty: false,
                  paymentProvider: null,
                  iscredit: false,
                  isGiftCard: false,
                  isDefault: "N",
                  csCurrencyId: "",
                  isoCode: "AED",
                  isactive: true,
                  salesType: [],
                },
              ]
            : []),
        ];
      }
      const isPreOrderPage = window.location.pathname.includes("/preorder");
      if (isPreOrderPage) {
        const isRefundScenario = amount < 0 && selectedPaymentMethod?.name?.toLowerCase() === "cash";
        const maxPaymentsToShow = isRefundScenario ? 3 : 3;
        let payments =
          PaymentMethods.length > maxPaymentsToShow
            ? sortBy(PaymentMethods, "sequenceNo").slice(
                0,
                tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === maxPaymentsToShow
                  ? maxPaymentsToShow
                  : cart.layAway === "Y"
                    ? maxPaymentsToShow
                    : maxPaymentsToShow
              )
            : PaymentMethods;
        setShowPayments(payments);
        setStartIndex(cart.layAway === "Y" && payments.length >= maxPaymentsToShow ? maxPaymentsToShow : payments.length);
      } else {
        let payments =
          PaymentMethods.length > 5
            ? sortBy(PaymentMethods, "sequenceNo").slice(
                0,
                tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === 5 ? 5 : (cart.layAway === "Y" || cart?.creditSale === "Y") ? 4 : 5
              )
            : PaymentMethods;

        setShowPayments(payments);
        setStartIndex((cart.layAway === "Y" || cart?.creditSale === "Y") && payments.length >= 5 ? 5 : payments.length);
      }

      if (workflowRules.length > 0 && localCart.isReturn === true && localCart.items.length > 0 && !showPaymentMethods) {
        // BUG-1744: "Sales Return" approval now runs upfront from Sales History
        // (SMPurchaseReturnView), before this screen is even reached. If that gate
        // already granted approval for this return, don't prompt again here — just
        // consume the flag and proceed. Blind/receiptless returns never set this
        // flag, so they still go through the dispatchEvent gate below.
        const isPreApprovedSalesReturn = useTransactionStore.getState().salesReturnApproved === true && !localCart.isBlindReceipt;
        if (isPreApprovedSalesReturn) {
          useTransactionStore.getState().setSalesReturnApproved(false);
        }

        if (isPreApprovedSalesReturn) {
          setSelectedProductInCart({});
          openPaymentModalByCustomer(cart);
          setShowPaymentMethods(true);
          if (CWHardwareController) {
            CWPoleDisplay(cart, "Total to pay");
          } else {
            PoleDisplay(cart, "Total to pay");
          }
        } else {
          let salesReturnRuleFound = false;
          workflowRules.forEach((res) => {
            // Accept both legacy ("Sales Return Process") and the configurable
            // newer name ("Sales Return Approval"). Backends differ on which one
            // they expose in workflow rules. (Issue #13)
            if (res.eventName === "Sales Return Process" || res.eventName === "Sales Return Approval") {
              salesReturnRuleFound = true;
              dispatchEvent(res.eventName, res.ruleName, res.ruleCondition, approvers, null, (eventData) => {
                if (eventData?.approvalGranted) {
                  setSelectedProductInCart({});
                  openPaymentModalByCustomer(cart);
                  setShowPaymentMethods(true);
                  if (CWHardwareController) {
                    CWPoleDisplay(cart, "Total to pay");
                  } else {
                    PoleDisplay(cart, "Total to pay");
                  }
                }
              });
            }
          });
          // If no "Sales Return Process" rule was found, fall through to standard payment modal
          if (!salesReturnRuleFound) {
            setSelectedProductInCart({});
            openPaymentModalByCustomer(cart);
            setShowPaymentMethods(true);
            if (CWHardwareController) {
              CWPoleDisplay(cart, "Total to pay");
            } else {
              PoleDisplay(cart, "Total to pay");
            }
          }
        }
      } else if (localCart.items.length > 0) {
        setSelectedProductInCart({});
        openPaymentModalByCustomer(cart);
        setShowPaymentMethods(true);
        if (CWHardwareController) {
          CWPoleDisplay(cart, "Total to pay");
        } else {
          PoleDisplay(cart, "Total to pay");
        }
      }
      let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
      orderTimeDetails = {
        ...orderTimeDetails,
        paymentStartTime: dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss"),
      };

      usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);
    } catch (error) {
      console.error("Error handling cart total:", error);
    }
  };

  // ─── isValidAmount ───
  const isValidAmount = (amount) => {
    if (amount === null || amount === undefined) return false;
    const value = amount.toString().trim();
    if (value === "") return false;
    const number = Number(value);
    return !isNaN(number) && number !== 0;
  };

  // ─── modulo helper ───
  const modulo = (a, b) => {
    return a - b * Math.floor(a / b);
  };

  // ─── handlePayment ───
  const handlePayment = async ({
    selectedPaymentMethod,
    cart,
    amount,
    posConfig,
    tillData,
    isOnline,
    isExchange,
    showConfirm,
    requestPayment,
    processOrder,
    setShowCardPaymnteModal,
    setPaymentType,
    setCardPaymnetError,
    messages,
    t,
    isCardPaymentFlag = false,
    enableGiftCardCheck = false,
    enablePoleDisplay = false,
    enableSetNumb = false,
    setNumb,
    CWHardwareController,
    CWPoleDisplay,
    PoleDisplay,
  }) => {
    const paymentName = selectedPaymentMethod?.name?.toLowerCase();
    const parsedAmount = parseFloat(amount || 0);
    // Read FRESH state from stores to avoid stale closures
    const exchangeCartObj = useCartStore.getState().exchangeCart || JSON.parse(localStorage.getItem("exchangeCartObj") || "{}");
    const freshCart = isExchange && exchangeCartObj ? exchangeCartObj : cart;
    const cartPaid = parseFloat(freshCart?.paid || 0);
    const cartTotal = parseFloat(freshCart?.total || 0);
    // Always use fresh posConfig — closure version may be null/stale
    const freshPosConfig = posConfig || usePOSConfigStore.getState().posConfig;

    if (isCardPaymentFlag) return;

    // AllowPrice Edit at POS — block payment when any cart item requires
    // manual price entry but still has zero/missing price.
    {
      const isExchangeForPriceCheck = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
      const cartForPriceCheck = isExchangeForPriceCheck
        ? useCartStore.getState().exchangeCart
        : useCartStore.getState().cart || cart;
      const zeroPriceItems = cartForPriceCheck?.items?.filter(
        (item) => item.posPriceEdit === "Y" && (!item.realPrice || Number(item.realPrice) <= 0)
      );
      if (zeroPriceItems?.length > 0) {
        Modal.warning({
          title: "Price Required",
          content: (
            <div>
              <p style={{ marginBottom: 8 }}>Price has not been entered for the following products. Please enter a valid price to continue.</p>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#fafafa" }}>
                    <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #f0f0f0" }}>Product Name</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #f0f0f0" }}>Product Code</th>
                  </tr>
                </thead>
                <tbody>
                  {zeroPriceItems.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "6px 8px" }}>{item.name || "—"}</td>
                      <td style={{ padding: "6px 8px" }}>{item.value || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ),
          okText: "OK",
          cancelButtonProps: { style: { display: "none" } },
        });
        return;
      }
    }

    const cashSaleValidation = retailCustomerRules?.find((rule) => rule.condition === "Cash Sale Validation");
    const existingCashPaid = (freshCart?.payments || []).filter((p) => p.name?.toLowerCase() === "cash").reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

    if (paymentName === "cash" && cashSaleValidation?.value && existingCashPaid + parsedAmount > parseFloat(cashSaleValidation.value)) {
      const limitAmount = parseFloat(cashSaleValidation.value);

      const formattedLimit = limitAmount.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

      Modal.warning({
        title: "Cash Payment Restricted",
        content: `Cash payments above ₹${formattedLimit} are not allowed.
          Please select another payment method or split the payment.`,
        okText: "OK",
        cancelButtonProps: { style: { display: "none" } },
      });

      return;
    }

    if (paymentName === "cash" && cartPaid + parsedAmount >= cartTotal && freshPosConfig?.showPaymentSummary === "Y") {
      const confirmed = await showConfirm();
      if (!confirmed) return;
    }

    // UPI follows the same integrated/manual flow as Card so the manual-entry
    // form (transactionId, authorization code, etc.) gets a chance to render
    // for both methods. Without this, integrated UPI silently completed the
    // payment with no form when the cashier picked Manual Entry.
    //
    // Use substring matching — the Tab7 fix used `["card","upi"].includes(name)`
    // which required EXACT name match, so tenant payment methods named "Credit
    // Card", "Debit Card", "UPI Payment" etc. silently fell through and the
    // card popup never opened. (Tab8 issues #13/#14)
    const isCardOrUpi = !!paymentName && (paymentName.includes("card") || paymentName.includes("upi"));

    // This block used to open the Card Payment / Manual
    // Override popup unconditionally and rely on a later gate (addAmount's
    // overPaymentFlag, or completePayment's BUG-1890 guard once the cashier
    // hit "Process Payment") to catch overpayment after the fact. That let
    // the popup open first and showed the "Overpayment not allowed" warning
    // a second time when the user tried to proceed. Mirror addAmount()'s
    // non-cash overpayment formula (paymentController.js) here so the popup
    // never opens on an overpayment and the warning shows exactly once.
    const isExchangeModeForCard = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
    const exchangeCartDataForCard = isExchangeModeForCard ? useCartStore.getState().exchangeCart || {} : null;
    const netForCard = isExchangeModeForCard ? Math.abs((exchangeCartDataForCard?.total || 0) + (cart?.total || 0)) : Math.abs(cartTotal);
    const paidForCard = isExchangeModeForCard ? exchangeCartDataForCard?.paid || 0 : cartPaid;
    // BUG-2103: normalise both sides to the currency precision before comparing, matching
    // addAmount()'s gate. `parsedAmount` comes from the amount field, which line 227 seeds
    // as (cartTotal - paidSoFar).toFixed(precision) — so an exact-amount tender read as an
    // overpayment whenever the raw cart.total sat a float-epsilon below its rounded value.
    const cardCeiling = Number((netForCard + parseFloat(overPayedAmount || 0)).toFixed(precision));
    const isCardOverpaying = Number((paidForCard + parsedAmount).toFixed(precision)) > cardCeiling;

    if (selectedPaymentMethod?.name?.toLowerCase() === "card" && selectedPaymentMethod?.integratedPayment && freshPosConfig?.show_card_payment_popup === "Y") {
      if (isCardOverpaying) {
        message.warning(`${messages?.errors?.cart?.overpayment_not_allowed}`);
        return;
      }
      if (isValidAmount(amount)) {
        // Fresh dialog state for every new payment attempt — otherwise the previous
        // transaction's Manual Entry selection and any leftover timeout/error message
        // carry over into this one. Default is always "Process through EDC machine".
        setPaymentType("edc");
        setCardPaymnetError(false);
        setCardPaymnetStatus({ paymentStatus: false, message: "", paymentMode: selectedPaymentMethod?.name || "" });
        setShowCardPaymnteModal(true);
      } else {
        message.warning("Kindly enter the amount before proceeding");
        return;
      }
    } else if (selectedPaymentMethod?.name?.toLowerCase() === "card" && !selectedPaymentMethod?.integratedPayment && freshPosConfig?.show_card_payment_popup === "Y") {
      if (isCardOverpaying) {
        message.warning(`${messages?.errors?.cart?.overpayment_not_allowed}`);
        return;
      }
      if (isValidAmount(amount)) {
        setPaymentType("manual");
        setCardPaymnetError(true);
        setCardPaymnetStatus({ paymentStatus: false, message: "", paymentMode: selectedPaymentMethod?.name || "" });
        setShowCardPaymnteModal(true);
        return;
      } else {
        message.warning("Kindly enter the amount before proceeding");
        return;
      }
      // TSK-2934: paymentMethod.enablePaymentData (per-payment-method, from getTillData)
      // is a different flag from posConfig.enablePaymentData (Till-level, used above for
      // Card only) — do not confuse the two.
    } else if (selectedPaymentMethod?.enablePaymentData && selectedPaymentMethod?.paymentDataFields?.length > 0) {
      if (isValidAmount(amount)) {
        setPaymentDataPopupMethod(selectedPaymentMethod);
        setPaymentDataPopupAmount(parsedAmount);
        setShowPaymentDataPopup(true);
        return;
      } else {
        message.warning("Kindly enter the amount before proceeding");
        return;
      }
    }

    const denominations = tillData?.tillAccess?.csBunit?.currencies?.[0]?.denominations?.sort((a, b) => a.value - b.value) || [];

    const smallest = denominations?.[0]?.value || 1;
    const result = modulo(parsedAmount, smallest);

    const isGiftCardOrLoyalty = paymentName !== "loyalty" && paymentName !== "voucher" && paymentName !== "gift card";

    if (enableGiftCardCheck && !isGiftCardOrLoyalty) {
      return;
    }

    const isExchangeMode = useTransactionStore.getState().selectedReturnType === "EXCHANGE";

    const exchangeCartData = isExchangeMode ? useCartStore.getState().exchangeCart || {} : null;

    // BUG-1805: netForPayment/paidForPayment must combine the exchange cart's
    // total/paid with the RETURN cart's (cart.total/cart.paid), not with
    // cartTotal/cartPaid — those come from freshCart, which resolves to the
    // exchange cart itself whenever the caller passes isExchange=true (e.g.
    // SMPaymentBar's numpad buttons). Adding exchangeCartData.total to itself
    // silently dropped the return credit and doubled the amount the code
    // thought was still due, so once a cashier finished a split payment
    // (Bank Transfer + Cash) for the correct, displayed total, the DI-11
    // check below compared the real entered sum against this inflated target
    // and rejected an exact match. Mirror completePayment()'s formula
    // (paymentController.js) exactly — exchangeTotal + returnTotal — computed
    // fresh from both carts' live totals each call, so it never drifts from
    // whatever completePayment used to build the order, even if the exchange
    // cart's contents change between payments.
    const netForPayment = isExchangeMode
      ? Math.abs((exchangeCartData?.total || 0) + (cart?.total || 0))
      : Math.abs(cartTotal);

    const paidForPayment = isExchangeMode ? exchangeCartData?.paid || 0 : cartPaid;

    // if (parsedAmount <= 0 && !isExchange && !cart?.isReturn) {
    //   return; // No amount entered — do nothing
    // }

    if (netForPayment > Math.abs(paidForPayment)) {
      if (paymentName === "cash" && result !== 0) {
        message.warning(messages?.warnings?.payment?.payment_multiple_check ? `${messages.warnings.payment.payment_multiple_check} ${smallest}` : t?.("cash_payment_error"));
        return;
      }

      // BUG-1519: When offline billing is disabled, block payment if the backend
      // is unreachable. navigator.onLine (and the isOnline state derived from it)
      // is unreliable in browsers — it stays `true` on LAN-only / no-internet, so
      // the message showed in the desktop env but never in the browser. If
      // navigator.onLine is already false we're definitely offline; if it claims
      // online we actively probe the server to confirm real reachability.
      if (freshPosConfig?.disableOfflineBilling === "Y") {
        const reachable = navigator.onLine ? await isServerReachable(4000) : false;
        if (!reachable) {
          message.warning(`${messages?.warnings?.payment?.payment_network_check}`);
          return;
        }
      }

      if (enableSetNumb && setNumb) {
        setNumb(0);
      }

      // BUG-1563: await so we can read fresh Zustand state after payment and
      // auto-complete. Without await, handlePayment returned immediately after
      // firing requestPayment; rapid Enter presses all saw stale cart.paid=0
      // and were blocked by paymentInProgressRef, so processOrder was never
      // called after payment finished.
      await requestPayment(selectedPaymentMethod, parsedAmount);
      if (!isExchangeMode) {
        const freshCartAfter = useCartStore.getState().cart;
        const freshPaidAfter = parseFloat(freshCartAfter?.paid || 0);
        const freshTotalAfter = parseFloat(freshCartAfter?.total || 0);
        // BUG-1754: don't auto-complete an Advance/Layaway order just because
        // the tendered amount covers the total — that must go through the
        // explicit "Create Advance"/"Complete Order" button (SMPaymentBar.js),
        // which sets statusType/param correctly for the layaway flow.
        const isAdvanceOrder = freshCartAfter?.layAway === "Y" || freshCartAfter?.docType === "Advance";
        if (freshTotalAfter > 0 && freshPaidAfter >= freshTotalAfter && !isAdvanceOrder) {
          await processOrder();
        }
      }

      if (enablePoleDisplay && netForPayment > parsedAmount) {
        if (CWHardwareController) {
          CWPoleDisplay(cart, "Show due payment", parsedAmount);
        } else if (PoleDisplay) {
          PoleDisplay(cart, "Show due payment", parsedAmount);
        }
      }
    } else if (netForPayment === 0 && isExchangeMode) {
      await requestPayment(selectedPaymentMethod, parsedAmount);
    } else if (cart?.docType !== "Advance") {
      // [DI-11] Validate split payment total matches order total before completing
      const activePayments = isExchangeMode ? exchangeCartData?.payments || [] : freshCart?.payments || [];
      if (activePayments.length > 1) {
        const splitSum = activePayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
        const targetTotal = Math.abs(netForPayment);
        const diff = Math.abs(splitSum - targetTotal);
        if (diff > 0.01) {
          console.error("[DI-11] Split payment sum mismatch", { splitSum, targetTotal, diff, payments: activePayments.length });
          message.error("Payment amounts do not match order total. Please verify payment entries.");
          return;
        }
      }
      // BUG-1563: await keeps paymentProcessingRef locked until order completes.
      await processOrder();
    }
  };

  // ─── handleDeletePayment ───
  const handleDeletePayment = (payment) => {
    const authHeaders = getOAuthHeaders();
    let paid = 0;
    Promise.all(
      cart.payments.map(async (pay) => {
        if (payment.name.toLowerCase() === "gift card" && payment.finPaymentmethodId === pay.finPaymentmethodId) {
          let setAuthTokens;
          if (authHeaders && authHeaders.access_token) {
            setAuthTokens = authHeaders.access_token;
          }
          // BUG-2078: unique referenceNo per active entry (not the shared cart.referenceId)
          // prevents this reversal from overwriting a prior removal's transaction row.
          const reversalPayload = buildGiftCardReversalPayload(
            giftCardData,
            tillData?.tillAccess?.cwrTill?.cwrTillID,
            cart.customer?.cwrCustomerId
          );

          if (reversalPayload) {
            const response = await upsertGiftCardTransaction({ query: reversalPayload.query });
            if (response.data.data.upsertGiftCardTransaction.status == 200) {
              // Fully reversed — clear instead of pushing stale negative rows that a
              // later redemption's findIndex() could pick up in place of the live entry.
              setGiftCardData([]);
            }
          }
        } else if (payment.name.toLowerCase() === "loyalty" && payment.finPaymentmethodId === pay.finPaymentmethodId) {
          // handleLoyaltyPayment is called from parent context — delegated via callback
        } else if (payment.paymentEntryId && pay.paymentEntryId) {
          // Permanent fix (BUG-1829): match by the unique paymentEntryId assigned at creation
          // (paymentController.js addAmount) instead of shared `name`. Falls back to the legacy
          // name/authorization/transactionId heuristics below only when the id is missing (entries
          // created before this fix).
          if (pay.paymentEntryId !== payment.paymentEntryId) {
            paid = paid + parseFloat(pay.amount);
            return pay;
          }
        } else if (pay.name !== payment.name && payment.name.toLowerCase() !== "card" && payment.isBankTransfer !== "Y") {
          paid = paid + parseFloat(pay.amount);
          return pay;
        } else if (payment.name.toLowerCase() === "card" && (payment.authorization ? pay.authorization !== payment.authorization : true)) {
          paid = paid + parseFloat(pay.amount);
          return pay;
        } else if (payment.isBankTransfer === "Y" && (payment.transactionId ? pay.transactionId !== payment.transactionId : pay.name !== payment.name)) {
          paid = paid + parseFloat(pay.amount);
          return pay;
        }
      })
    )
      .then((results) => {
        const updatedPayments = results.filter((result) => result !== undefined);
        const updatedPaid = paid + (cart?.advancePayment || 0);
        const updatedTendered = payment?.name?.toLowerCase() === "cash" ? (cart.tenderedAmount || 0) - payment.amount : cart.tenderedAmount;
        setAmount(parseFloat((cart.total - updatedPaid).toFixed(pricePrecision)));
        if (!isExchange) {
          setCart({
            ...cart,
            payments: updatedPayments,
            paid: updatedPaid,
            tenderedAmount: updatedTendered,
          });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // ─── onChangeAmount ───
  const onChangeAmount = (event) => {
    let type = "3";
    let value = event.target.value;
    value = value.replace(/[^0-9.]/g, "");
    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts.slice(1).join("");
    }
    paymentProcess(type, value, setAmount, amount, cart, tillData, selectedPaymentMethod, setOverPayedAmount, setNumb, numb);
  };

  const handleAmount = (value) => {
    let type = "1";
    paymentProcess(type, value, setAmount, amount, cart, tillData, selectedPaymentMethod, setOverPayedAmount, setNumb, numb);
  };

  const handleCashPayment = (value) => {
    let type = "2";
    paymentProcess(type, value, setAmount, amount, cart, tillData, selectedPaymentMethod, setOverPayedAmount, setNumb, numb);
  };

  // ─── showConfirm ───
  const showConfirm = () => {
    return new Promise((resolve, reject) => {
      Modal.confirm({
        title: (
          <>
            <ExclamationCircleOutlined style={{ color: "#FAAD17" }} /> Confirm Payment Details
          </>
        ),
        icon: null,
        content: (
          <div
            style={{
              background: "#fff",
              height: "auto",
              width: "100%",
              border: "1px solid #fff",
              borderRadius: "7px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Row style={{ fontSize: "1em", width: "100%" }}>
              <Col span={12} style={styles.screenStyles.mainScreen.paymentsData}>
                <p>Bill Amount</p>
              </Col>
              <Col span={12} style={{ display: "flex", alignItems: "center", textAlign: "right", justifyContent: "end" }}>
                <p style={{ textAlign: "right", fontWeight: "400", color: "#0F0718" }}>{parseFloat(Math.abs(cart.total)).toFixed(pricePrecision)}</p>
              </Col>
            </Row>
            <Row style={{ fontSize: "1em", width: "100%" }}>
              <Col span={12} style={styles.screenStyles.mainScreen.paymentsData}>
                <p>Amount Tendered</p>
              </Col>
              <Col span={12} style={{ display: "flex", alignItems: "center", textAlign: "right", justifyContent: "end" }}>
                <p style={{ textAlign: "right", fontWeight: "400", color: "#0F0718" }}>{parseFloat(amount || 0).toFixed(pricePrecision)}</p>
              </Col>
            </Row>
            <Row style={{ fontSize: "1em", width: "100%" }}>
              <Col span={12} style={styles.screenStyles.mainScreen.paymentsData}>
                <p>Amount Received</p>
              </Col>
              <Col span={12} style={{ display: "flex", alignItems: "center", textAlign: "right", justifyContent: "end" }}>
                <p style={{ textAlign: "right", fontWeight: "400", color: "#0F0718" }}>{Math.abs(cart.total - cart.paid).toFixed(pricePrecision)}</p>
              </Col>
            </Row>
            <Row style={{ fontSize: "1em", width: "100%" }}>
              <Col span={12} style={styles.screenStyles.mainScreen.paymentsData}>
                <p>Change Due</p>
              </Col>
              <Col span={12} style={{ display: "flex", alignItems: "center", textAlign: "right", justifyContent: "end" }}>
                <p style={{ textAlign: "right", fontWeight: "400", color: "#0F0718" }}>
                  {cart.paid + parseFloat(amount || 0) - Math.abs(cart.total) > 0
                    ? (cart.paid + parseFloat(amount || 0) - Math.abs(Math.abs(cart.total) + Math.abs(overPayedAmount))).toFixed(pricePrecision)
                    : cart?.change <= 0
                      ? "0.00"
                      : cart?.change}
                </p>
              </Col>
            </Row>
          </div>
        ),
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        },
        cancelButtonProps: { style: { borderRadius: "5px" } },
        okButtonProps: { style: { borderRadius: "5px" } },
      });
    });
  };

  // ─── openPaymentModal / openPaymentModalByCustomer ───
  const openPaymentModal = async () => {
    if (tillLayout === 2) {
      setPaymentModalLocal(true);
    }
    setPaymentProcessFlag(true);
    openPaymentModalByCustomer();
  };

  const openPaymentModalByCustomer = (cartObj) => {
    paymentModalInputRef?.current?.select();
    paymentModalInputRef?.current?.focus();
    const cartData = cartObj ? { ...cartObj } : { ...cart };
    if (cartObj && (cart.iscoupon === true || cart.totalBillDicount)) {
      cartData.iscoupon = cart.iscoupon;
      cartData.totalBillDicount = cart.totalBillDicount;
      cartData.items = cart.items;
    }
    localStorage.setItem("totalDiscountsCartRef", JSON.stringify(cartData));
    setPaymentModalByCustomerState(false);
    if (cartData?.items?.length > 0) {
      db.pricingRules.toArray().then((pr) => {
        const pricingRuleCount = pr.length;
        if (pricingRuleCount > 0) {
          pr.sort((a, b) => {
            if (a.priority === null && b.priority === null) return 0;
            else if (a.priority === null) return 1;
            else if (b.priority === null) return -1;
            return parseInt(a.priority, 10) - parseInt(b.priority, 10);
          });
          const cartHasCoupon = cartData.iscoupon === true || cartData.couponInput?.length > 0 || cartData.items?.some((item) => item.iscoupon === true);

          for (let i = 0; i < pricingRuleCount; i++) {
            const pricingRule = pr[i];
            if (dateValidator(pricingRule.startDate, pricingRule.endDate) && pricingRule.iscoupon === "N" && (pricingRule.type === "TD" || pricingRule.type === "TDF")) {
              if (cartHasCoupon) continue;
              if (pricingRule.timeSpecific === "Y") {
                const weekDay = currentDay();
                const pStartTime = pricingRule.starttime.substring(11);
                const pEndTIme = pricingRule.endtime !== null ? pricingRule.endtime.substring(11) : dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss").substring(11);
                const starttime = timeStamp().substring(0, 10) + " " + pStartTime;
                const endtime = timeStamp().substring(0, 10) + " " + pEndTIme;
                if (timeValidator(starttime, endtime) && pricingRule[weekDay] === "Y") {
                  // processBillDiscounts is in parent scope — call via passed-in ref if needed
                }
              } else {
                // processBillDiscounts(pricingRule, cartData, false);
              }
            }
          }
        }
      });
    }
  };

  const closePaymentModal = () => {
    setPaymentModalLocal(false);
    setSelectedPaymentMethod("");
  };

  // ─── Loyalty ───
  const processOtpInput = () => {
    const { otp, paymentMethod, value, redeemPoints } = loyalityOtpData;
    setLoyalityOtpModalVisible(false);
    if (otp === loyaltyPaymentOtp) {
      message.success(`${messages.errors.loyalty.loyalty_points_redeemed}`);
      paymentMethod.redemptionPoints = parseInt(redeemPoints);
      processPayment(paymentMethod, value);
    } else {
      message.warning(`${messages.info.invalid_otp}`);
    }
    setLoyalityOtpData();
    setLoyaltyPaymentOtp();
  };

  const handleLoyalityInput = (value) => {
    if (value === "clear") {
      setLoyaltyInputValue("");
    } else if (value === "x") {
      setLoyaltyInputValue(`${loyaltyInputValue.toString().substring(0, loyaltyInputValue.toString().length - 1)}`);
    } else {
      setLoyaltyInputValue(`${loyaltyInputValue}${value}`);
    }
  };

  const checkLoyality = async () => {
    requestPayment(selectedPaymentMethod, parseFloat(loyaltyInputValue));
    setLoyalityOtpModalVisible(false);
    setLoyaltyInputValue(0);
  };

  // ─── useEffect: selectedPaymentMethod ───
  useEffect(() => {
    const fetchData = async () => {
      if (selectedPaymentMethod?.name?.toLowerCase() === "cash") {
        setDenaminationsKeyboard(true);
      }
      if (selectedPaymentMethod.isloyalty) {
        setPaymentModalLoyalityMessages({
          inputMessage: "Enter Points to Redeem",
          buttonText: "Redeem",
        });
        try {
          let loyaltyData = await db.loyalityData.toArray();
          const matchedLoyalty = loyaltyData.find(
            (loyalty) => loyalty?.name === cart?.customer?.loyaltyLevel?.name || loyalty?.loyaltylevelId === cart?.customer?.loyaltyLevel?.cwrLoyaltyLevelId
          );
          let cartObj = cart;
          let value = 0;
          if (posConfig?.enableLoyalty === "Y") {
            loyaltyData?.forEach((loyality) => {
              if (loyality.loyaltylevelId === cartObj?.customer?.loyaltyLevel?.cwrLoyaltyLevelId) {
                loyality.loyaltyRedemption?.forEach((redeemItem) => {
                  value = parseFloat(parseFloat((cart.total - cart.paid) / redeemItem.redemptionValue)?.toFixed(precision));
                });
              }
            });
          } else {
            value = parseFloat(parseFloat(cart.total - cart.paid)?.toFixed(precision));
          }
          const redemptionRule = matchedLoyalty?.loyaltyRedemption?.[0];
          if (!redemptionRule || redemptionRule.minPurchase <= cart.total) {
            setLoyalityOtpModalVisible(true);
            setLoyaltyInputValue(value ? value : 0);
            upsertPOSLog(cart, "LOR");
          } else {
            message.error(`${messages.errors.loyalty.minimum_purchase_not_met}`);
          }
        } catch (error) {
          console.error("Error fetching loyalty data:", error);
        }
      } else if (selectedPaymentMethod.isGiftCard) {
        setGiftCardFlag(true);
        safeSetTimeout(() => {
          if (redeemNumberRef?.current) {
            redeemNumberRef.current.focus();
          }
        }, 250);
        setPaymentModalLoyalityMessages({
          inputMessage: "Enter Coupon Code",
          buttonText: "Redeem",
        });
      } else {
        setPaymentModalLoyalityMessages({
          inputMessage: "Enter Amount",
          buttonText: "Add Payment",
        });
      }
    };

    fetchData();
  }, [selectedPaymentMethod]);

  // ─── requestPayment ───
  const requestPayment = async (paymentMethod, value, grant, wallet, cardPaymentDetails, updatedCart) => {
    if (paymentInProgressRef.current) return;
    paymentInProgressRef.current = true;
    try {
      await addAmount(
        paymentMethod,
        value,
        grant,
        updatedCart || cart,
        setCart,
        setLoader,
        setPaymentModalLocal,
        setAmount,
        setLoyalityOtpData,
        setLoyalityOtpModalVisible,
        setSelectedPaymentMethod,
        setPaytmQrCodeModalOpens,
        setQrCodeResponse,
        overPayedAmount,
        setShowCardPaymnteModal,
        setCardPaymnetError,
        setCardPaymnetStatus,
        setCardPaymnetStatusPLB,
        setLoading,
        setIsCardPaymentLoading,
        setLayoutType,
        setKioskUI,
        dispatchQRCodeEvent,
        closeQRCodeModal,
        dispatchCCAvenueEvent,
        closeCCAvenueModal,
        wallet,
        cardPaymentDetails,
        setIntegratedPaymentStatus,
        exchangeCart,
        setExchangeCart,
        processOrder,
        showCustomerWalletRef?.current,
        (...args) => handleWalletPostPaymentRef?.current?.(...args),
        isNewGiftCardFlagFromParent ?? isNewGiftCardFlag,
        showGiftCardTopupRef?.current,
        (...args) => handleGiftCardTopupPostPaymentRef?.current?.(...args)
      );
    } finally {
      paymentInProgressRef.current = false;
    }
  };

  // ─── processPayment ───
  const processPayment = (paymentMethod, value) => {
    completePayment(paymentMethod, value, cart, setCart, setAmount, setSelectedPaymentMethod);
  };

  // ─── TSK-2934: generic payment-data popup submit ───
  // Stashes the captured fields on the cart (mirrors the existing
  // cardPaymentData.manual idiom — see paymentController.js addAmount()),
  // then resumes the normal add-payment flow exactly as if handlePayment
  // had fallen through without opening the popup.
  const handlePaymentDataSubmit = async (paymentData) => {
    const method = paymentDataPopupMethod;
    const value = paymentDataPopupAmount;
    setShowPaymentDataPopup(false);
    setPaymentDataPopupMethod(null);
    if (!method) return;
    const activeCart = useCartStore.getState().cart || cart;
    // F-07: write through setCart instead of mutating the store object in place,
    // and pass the updated cart straight to requestPayment (as updatedCart) since
    // the `cart` closure variable here won't reflect this setCart until next render.
    const updatedCart = {
      ...activeCart,
      paymentReferenceData: { ...(activeCart.paymentReferenceData || {}), [method.finPaymentmethodId]: paymentData },
    };
    setCart(updatedCart);
    await requestPayment(method, value, undefined, undefined, undefined, updatedCart);
    const freshCartAfter = useCartStore.getState().cart;
    const freshPaidAfter = parseFloat(freshCartAfter?.paid || 0);
    const freshTotalAfter = parseFloat(freshCartAfter?.total || 0);
    const isAdvanceOrder = freshCartAfter?.layAway === "Y" || freshCartAfter?.docType === "Advance";
    if (freshTotalAfter > 0 && freshPaidAfter >= freshTotalAfter && !isAdvanceOrder) {
      await processOrder();
    }
  };

  return {
    // TSK-2934: generic payment-data popup
    showPaymentDataPopup,
    setShowPaymentDataPopup,
    paymentDataPopupMethod,
    paymentDataPopupAmount,
    handlePaymentDataSubmit,
    // State
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    denaminationsKeyboard,
    setDenaminationsKeyboard,
    paymentProcessFlag,
    setPaymentProcessFlag,
    isShowingAll,
    setIsShowingAll,
    showPayments,
    setShowPayments,
    startIndex,
    setStartIndex,
    filteredPaymentMethods,
    loyaltyPaymentOtp,
    setLoyaltyPaymentOtp,
    loyaltyInputValue,
    setLoyaltyInputValue,
    loyalityOtpModalVisible,
    setLoyalityOtpModalVisible,
    loyalityOtpData,
    setLoyalityOtpData,
    paymentModalLoyalityMessages,
    setPaymentModalLoyalityMessages,
    paymentModalByCustomerState,
    setPaymentModalByCustomerState,
    giftCardFlag,
    setGiftCardFlag,
    isGiftCardFlag,
    setIsGiftCardFlag,
    isWalletFlag,
    setIswalletFlag,
    isNewGiftCardFlag,
    setIsNewGiftCardFlag,
    giftCardMode,
    setGiftCardMode,
    isBankTransferOpen,
    setIsBankTransferOpen,
    bankTransferContext,
    setBankTransferContext,
    initialShowPayments,
    paymentModal,
    setPaymentModalLocal,

    // Functions
    handleCartTotalClick,
    filterPaymentsBySaleType,
    getFilteredPaymentMethods,
    handlePayment,
    isValidAmount,
    handleDeletePayment,
    requestPayment,
    processPayment,
    onChangeAmount,
    handleAmount,
    handleCashPayment,
    showConfirm,
    openPaymentModal,
    openPaymentModalByCustomer,
    closePaymentModal,
    processOtpInput,
    handleLoyalityInput,
    checkLoyality,
  };
};

export default usePaymentFlow;
